/**
 * Голосовой режим Игротеки на сайте — тот же разговор, что на колонке с Алисой.
 *
 * Страница говорит с сервером (POST /api/voice-games/turn) фразами: сказанной
 * в микрофон (WebSpeechSession) или набранной. Ответ — текст для истории и
 * очередь сегментов: фраза, звук игры, пауза.
 *
 * Фразу читает голос нейросети Silero (POST /api/voice-games/speech): сервер
 * отдаёт запись и время каждого слова, и сцена подсвечивает слово, звучащее
 * сейчас. Персонажи (детектив) говорят своими голосами — сегмент несёт
 * `role: character` и `voice`. Демона нет или он не ответил — фразу читает
 * голос браузера (speechSynthesis), подсветка идёт по его событиям boundary.
 *
 * Плеер: ▶/⏸ (пробел), ⏮ ⏭ — фраза назад/вперёд (стрелки), ■ — стоп (Esc),
 * ⟲ — сначала, клик по слову — читать с него, скорость 0.8–2× (playbackRate,
 * время слов остаётся верным), голос ведущего — на выбор. Пока звучит одна
 * фраза, следующие две уже синтезируются.
 *
 * Для кого: незрячему без колонки и зрячему, который хочет сыграть «с
 * закрытыми глазами» или читать вместе с голосом. История — живая область
 * (её читает TalkBack/VoiceOver), пока чтение вслух выключено; когда читает
 * Silero, область молчит, чтобы не было двух голосов сразу.
 *
 * Корень — элемент [data-vgc]; атрибуты: data-endpoint, data-speech (пусто —
 * голос браузера), data-game, data-narrator, data-voices, data-i18n.
 * Плоский ES5, без сборки; вешает window.__voiceGameConsole (для тестов и
 * папки дела детектива).
 */
(function (global, doc) {
    'use strict';

    var SLUG = 'voice-games';
    var PREFETCH = 2;                        /* фраз синтезируется наперёд */
    var READY_TIMEOUT_MS = 15000;            /* дольше — фраза уходит голосу браузера */
    var SOUND_WATCHDOG_MS = 130000;          /* звуки игр короче двух минут (потолок Диалогов) */
    var SPEECH_WATCHDOG_MS_PER_CHAR = 85;    /* «end» у speechSynthesis иногда не приходит вовсе */
    var SPEECH_WATCHDOG_BASE_MS = 2500;

    /* Голос браузера (запасной путь): персонажу хотя бы своя высота и темп. */
    var FX = {
        pitch_down:      { pitch: 0.55, rate: 0.95 },
        hamster:         { pitch: 1.9,  rate: 1.2 },
        megaphone:       { pitch: 1.15, rate: 1.05 },
        behind_the_wall: { pitch: 0.85, rate: 0.9, volume: 0.6 },
        train_announce:  { pitch: 0.8,  rate: 0.9 },
        psychodelic:     { pitch: 1.5,  rate: 0.85 },
        pulse:           { pitch: 1.0,  rate: 1.25 }
    };

    /* Оттенки реплик персонажей (по голосу — один голос, один цвет). */
    var CHAR_COLORS = ['#f59e0b', '#a78bfa', '#34d399', '#f472b6', '#60a5fa', '#fb7185'];

    var ICON_NOTE = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3v10.55A4 4 0 1 0 14 17V7h4V3h-6z"/></svg>';
    var ICON_PLAY = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>';
    var ICON_MIC = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 14a3 3 0 0 0 3-3V5a3 3 0 1 0-6 0v6a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V21h2v-3.08A7 7 0 0 0 19 11h-2z"/></svg>';

    function rid() {
        var a = new Uint8Array(12);
        try { (global.crypto || global.msCrypto).getRandomValues(a); } catch (e) {
            for (var i = 0; i < a.length; i++) a[i] = Math.floor(Math.random() * 256);
        }
        var s = '';
        for (var j = 0; j < a.length; j++) s += ('0' + a[j].toString(16)).slice(-2);
        return s;
    }

    function deviceId() {
        try {
            var v = global.localStorage.getItem('vg_device');
            if (v && /^[a-f0-9]{24}$/.test(v)) return v;
            v = rid();
            global.localStorage.setItem('vg_device', v);
            return v;
        } catch (e) {
            return '';
        }
    }

    function ruVoice() {
        if (!global.speechSynthesis) return null;
        var list = [];
        try { list = global.speechSynthesis.getVoices() || []; } catch (e) { list = []; }
        var best = null;
        for (var i = 0; i < list.length; i++) {
            var v = list[i];
            if (!/^ru/i.test(v.lang || '')) continue;
            if (!best || (v.localService && !best.localService)) best = v;
        }
        return best;
    }

    function hashIndex(s, n) {
        var h = 0;
        for (var i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
        return Math.abs(h) % n;
    }

    /* Цвет реплик персонажа — по голосу; им же папка дела метит человека. */
    function charColor(voice) {
        return CHAR_COLORS[hashIndex(voice || 'x', CHAR_COLORS.length)];
    }

    /* Тот же разрез, что у сервера (WordTimes::tokens). */
    function tokens(say) {
        var t = String(say || '').trim();
        return t ? t.split(/\s+/) : [];
    }

    function absUrl(u) {
        try { return new URL(u, global.location.href).href; } catch (e) { return u; }
    }

    /* Время слов, когда сервер его не дал (или слова разошлись): по длине слов. */
    function evenTimes(toks, durMs) {
        var lead = 110, tail = 300, total = 0, i;
        for (i = 0; i < toks.length; i++) total += Math.max(1, toks[i].replace(/[^\wа-яё]/gi, '').length);
        var t = lead, span = Math.max(1, durMs - lead - tail), out = [];
        for (i = 0; i < toks.length; i++) {
            var d = span * Math.max(1, toks[i].replace(/[^\wа-яё]/gi, '').length) / (total || 1);
            out.push([Math.round(t), Math.round(t + d)]);
            t += d;
        }
        return out;
    }

    function Console(root) {
        this.root = root;
        this.endpoint = root.getAttribute('data-endpoint');
        this.speechEndpoint = root.getAttribute('data-speech') || '';
        /* Подписи — из resources/lang/ru/tools/voice-games.php (атрибут data-i18n компонента). */
        this.T = {};
        try { this.T = JSON.parse(root.getAttribute('data-i18n') || '{}') || {}; } catch (e) { this.T = {}; }
        this.voices = [];
        try { this.voices = JSON.parse(root.getAttribute('data-voices') || '[]') || []; } catch (e) { this.voices = []; }
        this.narrator = root.getAttribute('data-narrator') || '';
        this.game = root.getAttribute('data-game') || '';
        this.conv = rid();
        this.device = deviceId();
        this.fresh = true;
        this.busy = false;
        this.left = false;
        this.session = null;
        this.listening = false;
        this.rate = 1;
        this.meta = {};                      /* голос|тон|подпись → {url, dur, t} */
        this.replies = [];
        this.current = null;                 /* реплика на сцене */
        this.idx = 0;                        /* сегмент реплики, который звучит */
        this.word = -1;                      /* слово фразы, которое звучит */
        this.state = 'idle';                 /* idle | loading | playing | paused | done | stopped */
        this.token = 0;                      /* каждый новый запуск очереди — новый номер */
        this.runner = null;                  /* {pause, resume, stop} того, что звучит */
        this.pending = null;                 /* запуск, отложенный паузой во время подготовки */
        this.raf = 0;
        this.lastOn = null;

        this.$ = function (sel) { return root.querySelector(sel); };
        this.log = this.$('[data-vgc-log]');
        this.form = this.$('[data-vgc-form]');
        this.input = this.$('[data-vgc-input]');
        this.mic = this.$('[data-vgc-mic]');
        this.micLabelEl = this.$('[data-vgc-mic-label]');
        this.hero = this.$('[data-vgc-hero]');
        this.startBtn = this.$('[data-vgc-start]');
        this.stage = this.$('[data-vgc-stage]');
        this.stageText = this.$('[data-vgc-text]');
        this.stateEl = this.$('[data-vgc-state]');
        this.progressEl = this.$('[data-vgc-progress]');
        this.playBtn = this.$('[data-vgc-play]');
        this.prevBtn = this.$('[data-vgc-prev]');
        this.nextBtn = this.$('[data-vgc-next]');
        this.stopBtn = this.$('[data-vgc-stop]');
        this.replayBtn = this.$('[data-vgc-replay]');
        this.rateSel = this.$('[data-vgc-rate]');
        this.voiceSel = this.$('[data-vgc-voice]');
        this.previewBtn = this.$('[data-vgc-preview]');
        this.newBtn = this.$('[data-vgc-new]');
        this.chips = this.$('[data-vgc-chips]');
        this.speakToggle = this.$('[data-vgc-speak]');
        this.listenToggle = this.$('[data-vgc-listen]');
        this.status = this.$('[data-vgc-status]');
        this.audio = null;

        this.bind();
    }

    /* ─── Разметка и события ─────────────────────────────────── */

    Console.prototype.bind = function () {
        var self = this;
        if (this.startBtn) {
            this.startBtn.addEventListener('click', function () {
                self.unlockAudio();
                if (self.hero) self.hero.hidden = true;
                self.root.classList.add('vgc-live');
                self.send({ text: '' });
            });
        }
        this.form.addEventListener('submit', function (e) {
            e.preventDefault();
            var t = (self.input.value || '').trim();
            if (!t || self.busy) return;
            self.unlockAudio();
            self.input.value = '';
            self.send({ text: t, via: 'text' });
        });
        if (this.mic) {
            this.mic.addEventListener('click', function () {
                self.unlockAudio();
                if (self.listening) { self.stopListening(); return; }
                self.stop();
                self.listen();
            });
        }
        if (this.playBtn) this.playBtn.addEventListener('click', function () { self.unlockAudio(); self.toggle(); });
        if (this.stopBtn) this.stopBtn.addEventListener('click', function () { self.stop(); self.stopListening(); });
        if (this.replayBtn) this.replayBtn.addEventListener('click', function () { self.unlockAudio(); self.replay(); });
        if (this.prevBtn) this.prevBtn.addEventListener('click', function () { self.unlockAudio(); self.step(-1); });
        if (this.nextBtn) this.nextBtn.addEventListener('click', function () { self.unlockAudio(); self.step(1); });
        if (this.rateSel) {
            this.rateSel.addEventListener('change', function () {
                var r = parseFloat(self.rateSel.value);
                self.rate = r > 0 ? r : 1;
                if (self.audio) { try { self.audio.playbackRate = self.rate; } catch (e) {} }
            });
        }
        if (this.voiceSel) {
            this.voiceSel.addEventListener('change', function () { self.setNarrator(self.voiceSel.value); });
        }
        if (this.previewBtn) this.previewBtn.addEventListener('click', function () { self.unlockAudio(); self.preview(); });
        if (this.newBtn) {
            this.newBtn.addEventListener('click', function () {
                self.unlockAudio();
                self.stop();
                self.stopListening();
                self.conv = rid();
                self.fresh = true;
                self.left = false;
                self.log.innerHTML = '';
                self.replies = [];
                if (self.hero) self.hero.hidden = true;
                self.turns = 0;
                self.send({ text: '' });
            });
        }
        if (this.stageText) {
            this.stageText.addEventListener('click', function (e) {
                var w = e.target && e.target.closest ? e.target.closest('.vgc-w') : null;
                if (!w || !self.current) return;
                var ph = w.parentNode;
                var i = parseInt(ph.getAttribute('data-i'), 10);
                var k = parseInt(w.getAttribute('data-k'), 10);
                if (isNaN(i) || isNaN(k)) return;
                self.unlockAudio();
                self.playFrom(i, k);
            });
        }
        if (this.speakToggle) this.speakToggle.addEventListener('change', function () { self.syncLive(); if (!self.speakToggle.checked) self.stop(); });

        doc.addEventListener('keydown', function (e) {
            if (e.key === 'Escape' && (self.state === 'playing' || self.state === 'loading' || self.listening)) {
                self.stop();
                self.stopListening();
                return;
            }
            if (!self.current || !self.root.contains(doc.activeElement)) return;
            var tag = (doc.activeElement && doc.activeElement.tagName) || '';
            if (window.__keyOwnedByFocus ? window.__keyOwnedByFocus(e) : (/^(INPUT|TEXTAREA|SELECT|BUTTON|A|SUMMARY)$/.test(tag) || (doc.activeElement && doc.activeElement.isContentEditable))) return;
            if (e.key === ' ' || e.key === 'Spacebar' || e.key === 'k') { e.preventDefault(); self.unlockAudio(); self.toggle(); }
            else if (e.key === 'ArrowLeft') { e.preventDefault(); self.unlockAudio(); self.step(-1); }
            else if (e.key === 'ArrowRight') { e.preventDefault(); self.unlockAudio(); self.step(1); }
        });
        global.addEventListener('pagehide', function () { self.stop(); });

        if (global.speechSynthesis && typeof global.speechSynthesis.addEventListener === 'function') {
            try { global.speechSynthesis.addEventListener('voiceschanged', function () { self.syncSpeechAvailability(); }); } catch (e) {}
        }
        this.syncSpeechAvailability();
        this.syncLive();
        if (!(global.WebSpeechSession && global.WebSpeechSession.isSupported && global.WebSpeechSession.isSupported())) {
            if (this.mic) this.mic.hidden = true;
            var lab = this.listenToggle && this.listenToggle.closest ? this.listenToggle.closest('label') : null;
            if (lab) lab.hidden = true;
        }
    };

    /* Настройки, сохранённые __toolSettings (вызывается из boot после bind). */
    Console.prototype.applySettings = function () {
        if (this.voiceSel) {
            var ok = false;
            for (var i = 0; i < this.voiceSel.options.length; i++) if (this.voiceSel.options[i].value === this.voiceSel.value) ok = true;
            if (!ok || !this.voiceSel.value) this.voiceSel.value = this.narrator;
            this.narrator = this.voiceSel.value || this.narrator;
        }
        if (this.rateSel) {
            var r = parseFloat(this.rateSel.value);
            if (!(r > 0)) { this.rateSel.value = '1'; r = 1; }
            this.rate = r;
        }
        this.syncLive();
    };

    Console.prototype.charColor = charColor;

    Console.prototype.speakOn = function () {
        return !this.speakToggle || this.speakToggle.checked;
    };

    /* Пока читает голос, история не объявляется вслух — иначе два голоса разом. */
    Console.prototype.syncLive = function () {
        if (this.log) this.log.setAttribute('aria-live', this.speakOn() ? 'off' : 'polite');
    };

    Console.prototype.syncSpeechAvailability = function () {
        var note = this.$('[data-vgc-novoice]');
        if (note) note.hidden = !!this.speechEndpoint || !!ruVoice() || !global.speechSynthesis;
    };

    Console.prototype.unlockAudio = function () {
        var gate = global.__audioGate;
        if (!gate) return;
        try {
            if (!this.audio) this.audio = gate.element();
            gate.unlockNow(this.audio);
        } catch (e) {}
    };

    Console.prototype.audioEl = function () {
        if (!this.audio) {
            var gate = global.__audioGate;
            this.audio = gate ? gate.element() : new Audio();
        }
        return this.audio;
    };

    Console.prototype.setStatus = function (msg) {
        if (this.status) this.status.textContent = msg || '';
    };

    Console.prototype.setNarrator = function (v) {
        var ok = false;
        for (var i = 0; i < this.voices.length; i++) if (this.voices[i].id === v) ok = true;
        if (!ok) return;
        this.narrator = v;
        /* Ещё не прозвучавшие фразы ведущего — новым голосом. */
        for (var r = 0; r < this.replies.length; r++) {
            var items = this.replies[r].items;
            for (var j = 0; j < items.length; j++) {
                if (items[j].kind === 'say' && items[j].role !== 'character' && items[j].usedVoice !== v) items[j].ready = null;
            }
        }
    };

    Console.prototype.preview = function () {
        var v = null;
        for (var i = 0; i < this.voices.length; i++) if (this.voices[i].id === this.narrator) v = this.voices[i];
        if (!v || !v.preview) return;
        this.stop();
        var gate = global.__audioGate;
        var el = this.audioEl();
        if (gate) gate.play(el, v.preview).catch(function () {});
    };

    /* ─── История ────────────────────────────────────────────── */

    Console.prototype.addLine = function (who, text, reply) {
        var self = this;
        var p = doc.createElement('div');
        p.className = 'vgc-line vgc-' + who;
        var body = doc.createElement('p');
        body.className = 'vgc-line-body';
        var b = doc.createElement('span');
        b.className = 'vgc-who';
        b.textContent = (who === 'me' ? this.T.me : this.T.game) || '';
        body.appendChild(b);
        body.appendChild(doc.createTextNode(text));
        p.appendChild(body);
        if (reply) {
            var btn = doc.createElement('button');
            btn.type = 'button';
            btn.className = 'vgc-icon';
            btn.setAttribute('aria-label', this.T.replay_line || '');
            btn.title = this.T.replay_line || '';
            btn.innerHTML = ICON_PLAY;
            btn.addEventListener('click', function () {
                self.unlockAudio();
                self.show(reply);
                self.playFrom(0, 0);
            });
            p.appendChild(btn);
        }
        this.log.appendChild(p);
        /* Внутренняя прокрутка истории, а не страницы. */
        this.log.scrollTop = this.log.scrollHeight;
    };

    Console.prototype.renderChips = function (buttons) {
        var self = this;
        this.chips.innerHTML = '';
        (buttons || []).forEach(function (b) {
            var el;
            if (b.url) {
                el = doc.createElement('a');
                el.href = b.url;
                el.className = 'vgc-chip';
                el.textContent = b.title;
                el.rel = 'noopener';
            } else {
                el = doc.createElement('button');
                el.type = 'button';
                el.className = 'vgc-chip';
                el.textContent = b.title;
                el.addEventListener('click', function () {
                    if (self.busy) return;
                    self.unlockAudio();
                    self.stop();
                    self.send({ text: b.title, payload: b.payload || {}, via: 'chip' });
                });
            }
            self.chips.appendChild(el);
        });
    };

    /* ─── Ход ────────────────────────────────────────────────── */

    Console.prototype.send = function (msg) {
        var self = this;
        if (this.busy) return;
        this.busy = true;
        this.root.setAttribute('aria-busy', 'true');
        if (msg.text) this.addLine('me', msg.text, null);
        this.setStatus('');

        var body = {
            conv: this.conv,
            device: this.device || null,
            text: msg.text || '',
            payload: msg.payload || null,
            game: this.fresh ? (this.game || null) : null,
            fresh: this.fresh,
            voice: this.narrator || null
        };
        this.fresh = false;

        fetch(this.endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify(body),
            credentials: 'omit'
        }).then(function (r) {
            if (!r.ok) throw new Error('HTTP ' + r.status);
            return r.json();
        }).then(function (data) {
            self.busy = false;
            self.root.removeAttribute('aria-busy');
            var reply = self.buildReply(data);
            self.replies.push(reply);
            self.addLine('game', data.text || '', reply.items.length ? reply : null);
            self.renderChips(data.buttons);
            self.left = !!data.leave;
            if (data.leave && self.newBtn) self.newBtn.focus();
            self.show(reply);
            self.playFrom(0, 0, true);
            self.track(data, msg);
        }).catch(function (err) {
            self.busy = false;
            self.root.removeAttribute('aria-busy');
            self.setStatus(self.T.err_network);
            if (global.__aeDiagnostic) { try { global.__aeDiagnostic({ issue: 'voice_games_turn_failed', m: String(err && err.message || err).slice(0, 120) }); } catch (e) {} }
        });
    };

    /* Телеметрия страницы игры (слаг — из meta tool-slug, его ставит __aeFeature).
       Каждую реплику и так пишет сервер (feature_use{f:voice_game}), здесь — то,
       чего он не видит: начал ли человек играть на ЭТОЙ странице, чем отвечает
       (голос / текст / кнопка — одно событие на способ) и доиграл ли партию. */
    Console.prototype.track = function (data, msg) {
        var g = data.game || this.game || '';
        this.turns = (this.turns || 0) + 1;
        this.vias = this.vias || {};
        try {
            if (this.turns === 1 && global.__stage) global.__stage('playing', { g: g });
            var via = msg.via || '';
            if (via && !this.vias[via] && global.__aeFeature) {
                this.vias[via] = 1;
                global.__aeFeature('vg_input', { g: g, via: via });
            }
            if (data.over && global.__aeToolComplete) {
                global.__aeToolComplete({ t: 'vg_over', g: g, turns: this.turns, voice: this.vias.voice ? 1 : 0 });
            }
        } catch (e) {}
    };

    Console.prototype.buildReply = function (data) {
        var items = [];
        (data.segments || []).forEach(function (seg) {
            if (seg.say) {
                items.push({
                    kind: 'say', say: seg.say, tts: seg.tts || seg.say, sig: seg.sig || '',
                    role: seg.role === 'character' ? 'character' : 'narrator',
                    voice: seg.voice || null, pitch: seg.pitch || 0, fx: seg.fx || null,
                    tokens: tokens(seg.say), words: [], ready: null, meta: null
                });
            } else if (seg.snd) {
                items.push({ kind: 'snd', url: seg.snd });
            } else if (seg.pause) {
                items.push({ kind: 'pause', ms: Math.min(10000, seg.pause | 0) });
            }
        });
        var total = 0;
        items.forEach(function (it) { if (it.kind === 'say') { it.before = total; total += it.tokens.length; } });
        return { text: data.text || '', items: items, words: total };
    };

    /* ─── Сцена ──────────────────────────────────────────────── */

    Console.prototype.show = function (reply) {
        if (this.current === reply && this.stage && !this.stage.hidden) return;
        this.stop();
        this.current = reply;
        if (this.stage) this.stage.hidden = false;
        if (this.hero) this.hero.hidden = true;
        var frag = doc.createDocumentFragment();
        reply.items.forEach(function (it, i) {
            if (it.kind === 'say') {
                var ph = doc.createElement('span');
                ph.className = 'vgc-ph' + (it.role === 'character' ? ' vgc-ph-char' : '');
                ph.setAttribute('data-i', String(i));
                if (it.role === 'character') ph.style.setProperty('--vgc-char', charColor(it.voice));
                it.words = [];
                it.tokens.forEach(function (tok, k) {
                    if (k) ph.appendChild(doc.createTextNode(' '));
                    var w = doc.createElement('span');
                    w.className = 'vgc-w';
                    w.setAttribute('data-k', String(k));
                    w.textContent = tok;
                    ph.appendChild(w);
                    it.words.push(w);
                });
                frag.appendChild(ph);
                frag.appendChild(doc.createTextNode(' '));
            } else if (it.kind === 'snd') {
                var s = doc.createElement('span');
                s.className = 'vgc-snd';
                s.setAttribute('aria-hidden', 'true');
                s.innerHTML = ICON_NOTE;
                it.node = s;
                frag.appendChild(s);
                frag.appendChild(doc.createTextNode(' '));
            }
        });
        this.stageText.textContent = '';
        this.stageText.appendChild(frag);
        this.paintAll(-1);
        this.setState('idle');
    };

    /* Слова до (idx, word) — прочитаны, остальные — нет. */
    Console.prototype.paintAll = function (upto) {
        if (!this.current) return;
        var items = this.current.items;
        for (var i = 0; i < items.length; i++) {
            var it = items[i];
            if (it.kind === 'snd' && it.node) it.node.classList.remove('is-on');
            if (it.kind !== 'say') continue;
            for (var k = 0; k < it.words.length; k++) {
                it.words[k].classList.toggle('is-done', i < upto);
                it.words[k].classList.remove('is-on');
            }
        }
        this.lastOn = null;
        this.progress(upto >= items.length ? 1 : (upto < 0 ? 0 : null));
    };

    Console.prototype.highlight = function (it, k) {
        if (k === this.word && this.lastOn) return;
        this.word = k;
        if (this.lastOn) this.lastOn.classList.remove('is-on');
        for (var x = 0; x < it.words.length; x++) it.words[x].classList.toggle('is-done', x < k || (x === k));
        var w = it.words[k];
        if (w) {
            w.classList.add('is-on');
            this.lastOn = w;
        }
        if (this.current && this.current.words) this.progress((it.before + k + 1) / this.current.words);
    };

    Console.prototype.progress = function (f) {
        if (!this.progressEl || f === null || f === undefined) return;
        this.progressEl.style.width = Math.max(0, Math.min(1, f)) * 100 + '%';
    };

    Console.prototype.setState = function (s, item) {
        this.state = s;
        var c = this.root.classList;
        c.toggle('vgc-playing', s === 'playing');
        c.toggle('vgc-loading', s === 'loading');
        var T = this.T;
        var label = '';
        if (s === 'playing' && item && item.kind === 'pause') label = this.stateEl ? this.stateEl.textContent : '';
        else if (s === 'loading') label = T.state_loading;
        else if (s === 'playing') label = item && item.kind === 'snd' ? T.state_sound : (item && item.role === 'character' ? T.state_character : T.state_narrator);
        else if (s === 'paused') label = T.state_paused;
        else if (s === 'done') label = T.state_done;
        else if (s === 'stopped') label = T.state_stopped;
        if (this.stateEl) this.stateEl.textContent = label || '';
        if (this.playBtn) {
            var on = s === 'playing' || s === 'loading';
            this.playBtn.setAttribute('aria-label', (on ? T.pause : (s === 'paused' ? T.resume : T.play)) || '');
        }
    };

    /* ─── Плеер ──────────────────────────────────────────────── */

    Console.prototype.toggle = function () {
        if (!this.current) return;
        if (this.state === 'playing' || this.state === 'loading') this.pause();
        else if (this.state === 'paused') this.resume();
        else this.playFrom(0, 0);
    };

    Console.prototype.replay = function () {
        if (this.current) this.playFrom(0, 0);
    };

    /* Фраза назад/вперёд: назад с середины фразы — к её началу. */
    Console.prototype.step = function (dir) {
        if (!this.current) return;
        var items = this.current.items;
        var i = this.idx;
        if (dir < 0 && this.word > 1 && items[i] && items[i].kind === 'say') { this.playFrom(i, 0); return; }
        for (i += dir; i >= 0 && i < items.length; i += dir) {
            if (items[i].kind === 'say') { this.playFrom(i, 0); return; }
        }
        if (dir < 0) this.playFrom(0, 0);
    };

    Console.prototype.playFrom = function (i, k, auto) {
        if (!this.current) return;
        this.halt();
        this.idx = i;
        this.word = -1;
        this.paintAll(i);
        var token = ++this.token;
        this.auto = !!auto;
        this.prefetch(i);
        this.run(token, k || 0);
    };

    Console.prototype.pause = function () {
        if (this.state !== 'playing' && this.state !== 'loading') return;
        if (this.runner && this.runner.pause) this.runner.pause();
        this.paused = true;
        this.setState('paused');
        this.stopTicker();
    };

    Console.prototype.resume = function () {
        if (this.state !== 'paused') return;
        this.paused = false;
        var it = this.current && this.current.items[this.idx];
        this.setState(this.pending ? 'loading' : 'playing', it);
        if (this.pending) { var p = this.pending; this.pending = null; p(); return; }
        if (this.runner && this.runner.resume) this.runner.resume();
        else this.playFrom(this.idx, Math.max(0, this.word));
    };

    /* Остановить то, что звучит, не трогая сцену. */
    Console.prototype.halt = function () {
        this.token++;
        this.paused = false;
        this.pending = null;
        this.stopTicker();
        if (this.runner && this.runner.stop) { try { this.runner.stop(); } catch (e) {} }
        this.runner = null;
        try { if (global.speechSynthesis) global.speechSynthesis.cancel(); } catch (e) {}
    };

    Console.prototype.stop = function () {
        var was = this.state;
        this.halt();
        if (this.audio) { try { this.audio.pause(); } catch (e) {} }
        if (this.current && (was === 'playing' || was === 'loading' || was === 'paused')) {
            this.paintAll(-1);
            this.setState('stopped');
        } else if (!this.current) {
            this.setState('idle');
        }
    };

    /* Подготовить (синтезировать) фразы, начиная с i: не больше PREFETCH одновременно. */
    Console.prototype.prefetch = function (i) {
        var self = this;
        if (!this.current || !this.speechEndpoint || !this.speakOn()) return;
        var items = this.current.items;
        var queue = [];
        for (var j = i; j < items.length; j++) if (items[j].kind === 'say' && !items[j].ready) queue.push(items[j]);
        var running = 0;
        for (var r = 0; r < items.length; r++) if (items[r].kind === 'say' && items[r].loading) running++;
        function pump() {
            while (running < PREFETCH && queue.length) {
                var it = queue.shift();
                if (it.ready) continue;
                running++;
                self.prepare(it).then(function () { running--; pump(); });
            }
        }
        pump();
    };

    Console.prototype.voiceFor = function (it) {
        if (it.role !== 'character' || !it.voice) return this.narrator || it.voice;
        if (it.voice !== this.narrator) return it.voice;
        /* Ведущего выбрали голосом персонажа уже после хода — персонажу другой голос того же пола. */
        var g = null, i;
        for (i = 0; i < this.voices.length; i++) if (this.voices[i].id === it.voice) g = this.voices[i].gender;
        for (i = 0; i < this.voices.length; i++) {
            if (this.voices[i].id !== it.voice && (!g || this.voices[i].gender === g)) return this.voices[i].id;
        }
        return it.voice;
    };

    /** @return Promise<{url, dur, t}|null> */
    Console.prototype.prepare = function (it) {
        var self = this;
        if (it.ready) return it.ready;
        var voice = this.voiceFor(it);
        if (!this.speechEndpoint || !it.sig || !voice) { it.ready = Promise.resolve(null); return it.ready; }
        var key = voice + '|' + (it.pitch || 0) + '|' + it.sig;
        it.usedVoice = voice;
        if (this.meta[key]) { it.meta = this.meta[key]; it.ready = Promise.resolve(it.meta); return it.ready; }
        it.loading = true;
        it.ready = fetch(this.speechEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            credentials: 'omit',
            body: JSON.stringify({ text: it.tts, sig: it.sig, voice: voice, pitch: it.pitch || 0 })
        }).then(function (r) {
            if (!r.ok) throw new Error('HTTP ' + r.status);
            return r.json();
        }).then(function (m) {
            it.loading = false;
            if (!m || !m.url) throw new Error('no url');
            if (!m.t || m.t.length !== it.tokens.length) m.t = evenTimes(it.tokens, m.dur || 0);
            self.meta[key] = m;
            it.meta = m;
            return m;
        }).catch(function (err) {
            it.loading = false;
            it.ready = null;   /* следующее нажатие попробует снова */
            if (global.__aeDiagnostic) { try { global.__aeDiagnostic({ issue: 'voice_games_speech_failed', m: String(err && err.message || err).slice(0, 80) }); } catch (e) {} }
            return null;
        });
        return it.ready;
    };

    Console.prototype.run = function (token, k) {
        var self = this;
        if (token !== this.token || !this.current) return;
        var items = this.current.items;
        if (this.idx >= items.length) { this.finish(token); return; }
        var it = items[this.idx];
        var next = function () {
            if (token !== self.token) return;
            if (it.kind === 'say') for (var x = 0; x < it.words.length; x++) { it.words[x].classList.add('is-done'); it.words[x].classList.remove('is-on'); }
            self.lastOn = null;
            self.runner = null;
            self.idx++;
            self.word = -1;
            self.prefetch(self.idx);
            self.run(token, 0);
        };

        if (it.kind === 'pause') { this.setState('playing', it); this.runner = this.waitRunner(it.ms, next); return; }
        if (it.kind === 'snd') { this.setState('playing', it); this.runner = this.soundRunner(it, token, next); return; }
        if (!this.speakOn()) { next(); return; }

        var ready = it.ready || this.prepare(it);
        var settled = false;
        this.setState(this.paused ? 'paused' : 'loading', it);
        var start = function (m) {
            if (settled || token !== self.token) return;
            settled = true;
            var go = function () {
                if (token !== self.token) return;
                self.setState('playing', it);
                if (m && m.url) self.runner = self.speechRunner(it, m, k, token, next);
                else {
                    if (self.speechEndpoint && self.speakOn()) self.setStatus(self.T.fallback);
                    self.runner = self.browserRunner(it, k, token, next);
                }
            };
            if (self.paused) { self.pending = go; return; }
            go();
        };
        ready.then(start, function () { start(null); });
        setTimeout(function () { start(null); }, READY_TIMEOUT_MS);
    };

    Console.prototype.finish = function (token) {
        if (token !== this.token) return;
        this.runner = null;
        this.stopTicker();
        this.paintAll(this.current ? this.current.items.length : -1);
        this.setState('done');
        /* Ответ дочитан — сразу слушать человека (если он этого хочет). */
        if (this.auto && !this.left && this.listenToggle && this.listenToggle.checked) this.listen(true);
        this.auto = false;
    };

    /* Подсветка по часам записи — на каждый кадр. */
    Console.prototype.startTicker = function (it, m) {
        var self = this;
        this.stopTicker();
        var el = this.audioEl();
        var t = m.t || [];
        var raf = global.requestAnimationFrame || function (f) { return setTimeout(f, 50); };
        function tick() {
            var ms = el.currentTime * 1000;
            var k = -1;
            for (var x = 0; x < t.length; x++) {
                if (t[x] && t[x][0] <= ms) k = x;
                else if (t[x] && t[x][0] > ms) break;
            }
            if (k >= 0) self.highlight(it, k);
            self.raf = raf(tick);
        }
        this.raf = raf(tick);
    };

    Console.prototype.stopTicker = function () {
        if (!this.raf) return;
        var caf = global.cancelAnimationFrame || clearTimeout;
        caf(this.raf);
        this.raf = 0;
    };

    /* Фраза голосом Silero: запись + время слов. */
    Console.prototype.speechRunner = function (it, m, k, token, next) {
        var self = this;
        var el = this.audioEl();
        var at = k > 0 && m.t && m.t[k] ? Math.max(0, m.t[k][0] / 1000 - 0.05) : 0;
        var done = false;
        function cleanup() {
            el.removeEventListener('ended', onEnd);
            el.removeEventListener('error', onErr);
        }
        function onEnd() { if (done) return; done = true; cleanup(); self.stopTicker(); next(); }
        function onErr() {
            if (done) return;
            done = true;
            cleanup();
            self.stopTicker();
            if (token !== self.token) return;
            self.runner = self.browserRunner(it, Math.max(0, self.word), token, next);
        }
        el.addEventListener('ended', onEnd);
        el.addEventListener('error', onErr);
        var url = absUrl(m.url);
        try {
            if (el.src !== url) el.src = url;
            /* До загрузки метаданных это «позиция начала» (так велит спецификация). */
            el.currentTime = at;
        } catch (e) {}
        try { el.defaultPlaybackRate = this.rate; el.playbackRate = this.rate; } catch (e) {}
        this.playEl(el, token);
        this.startTicker(it, m);
        return {
            pause: function () { try { el.pause(); } catch (e) {} },
            resume: function () { self.playEl(el, token); self.startTicker(it, m); },
            stop: function () { done = true; cleanup(); try { el.pause(); } catch (e) {} }
        };
    };

    Console.prototype.playEl = function (el, token) {
        var self = this;
        var p;
        try { p = el.play(); } catch (e) { p = null; }
        if (p && p.catch) {
            p.catch(function (err) {
                if (token !== self.token) return;
                if (err && err.name === 'NotAllowedError') {
                    /* Разрешение на звук отозвано (звонок, другая вкладка) — ждём нажатия. */
                    if (global.__audioGate) { try { global.__audioGate.arm(el); } catch (e) {} }
                    self.pause();
                    self.setStatus(self.T.tap_to_play);
                }
            });
        }
    };

    /* Звук игры: целиком, с обычной скоростью. */
    Console.prototype.soundRunner = function (it, token, next) {
        var self = this;
        var el = this.audioEl();
        var done = false;
        var guard = setTimeout(finish, SOUND_WATCHDOG_MS);
        function finish() {
            if (done) return;
            done = true;
            clearTimeout(guard);
            el.removeEventListener('ended', finish);
            el.removeEventListener('error', finish);
            if (it.node) it.node.classList.remove('is-on');
            next();
        }
        if (it.node) it.node.classList.add('is-on');
        el.addEventListener('ended', finish);
        el.addEventListener('error', finish);
        try { el.defaultPlaybackRate = 1; el.playbackRate = 1; } catch (e) {}
        var url = absUrl(it.url);
        try { if (el.src !== url) el.src = url; el.currentTime = 0; } catch (e) {}
        this.playEl(el, token);
        return {
            pause: function () { try { el.pause(); } catch (e) {} },
            resume: function () { self.playEl(el, token); },
            stop: function () { done = true; clearTimeout(guard); el.removeEventListener('ended', finish); el.removeEventListener('error', finish); try { el.pause(); } catch (e) {} if (it.node) it.node.classList.remove('is-on'); }
        };
    };

    Console.prototype.waitRunner = function (ms, next) {
        var left = ms, started = Date.now(), timer = setTimeout(next, left);
        return {
            pause: function () { clearTimeout(timer); left -= Date.now() - started; },
            resume: function () { started = Date.now(); timer = setTimeout(next, Math.max(0, left)); },
            stop: function () { clearTimeout(timer); }
        };
    };

    /* Запасной путь: голос браузера, подсветка по событиям boundary. */
    Console.prototype.browserRunner = function (it, k, token, next) {
        var self = this;
        var voice = ruVoice();
        if (!global.speechSynthesis || !global.SpeechSynthesisUtterance || !voice) {
            var t = setTimeout(next, 400);
            return { stop: function () { clearTimeout(t); } };
        }
        var alive = true;
        var from = Math.max(0, k);
        var toks = it.tokens.slice(from);
        var text = toks.join(' ');
        var starts = [], pos = 0;
        for (var i = 0; i < toks.length; i++) { starts.push(pos); pos += toks[i].length + 1; }
        var watchdog = null;
        function speak() {
            var u = new global.SpeechSynthesisUtterance(text);
            u.lang = voice.lang || 'ru-RU';
            u.voice = voice;
            var f = (it.fx && FX[it.fx]) || null;
            u.rate = (f ? f.rate : 1.05) * self.rate;
            if (f) { u.pitch = f.pitch; if (f.volume) u.volume = f.volume; }
            else if (it.role === 'character') u.pitch = 1.25;
            u.onboundary = function (e) {
                if (!alive || (e.name && e.name !== 'word')) return;
                var x = 0;
                while (x + 1 < starts.length && starts[x + 1] <= e.charIndex) x++;
                self.highlight(it, from + x);
            };
            u.onend = u.onerror = function () { if (alive) { alive = false; clearTimeout(watchdog); next(); } };
            global.speechSynthesis.speak(u);
            watchdog = setTimeout(function () { if (alive) { alive = false; next(); } }, SPEECH_WATCHDOG_BASE_MS + text.length * SPEECH_WATCHDOG_MS_PER_CHAR / self.rate);
        }
        speak();
        return {
            /* pause()/resume() у speechSynthesis на Android ненадёжны: пауза — это
               отмена с запоминанием слова, продолжение — новая фраза с него. */
            pause: function () { alive = false; clearTimeout(watchdog); try { global.speechSynthesis.cancel(); } catch (e) {} },
            resume: function () { if (token !== self.token) return; self.runner = self.browserRunner(it, Math.max(from, self.word), token, next); },
            stop: function () { alive = false; clearTimeout(watchdog); try { global.speechSynthesis.cancel(); } catch (e) {} }
        };
    };

    /* ─── Микрофон ──────────────────────────────────────────── */

    Console.prototype.micLabel = function (on) {
        if (!this.mic) return;
        this.mic.setAttribute('aria-pressed', on ? 'true' : 'false');
        var t = (on ? this.T.mic_on : this.T.mic) || '';
        if (this.micLabelEl) this.micLabelEl.textContent = t;
        else this.mic.innerHTML = ICON_MIC + '<span>' + t + '</span>';
    };

    Console.prototype.listen = function (auto) {
        var self = this;
        if (this.listening || this.busy || !global.WebSpeechSession || !global.WebSpeechSession.isSupported()) return;
        var heard = '';
        var sent = false;
        this.session = new global.WebSpeechSession({
            slug: SLUG,
            lang: 'ru-RU',
            continuous: false,
            interimResults: true,
            autoRestart: false,
            onResult: function (r) {
                if (r.interim) self.input.placeholder = r.interim;
                if (r.final) heard = (heard + ' ' + r.final).trim();
            },
            onEnd: function () {
                self.listening = false;
                self.root.classList.remove('vgc-listening');
                self.micLabel(false);
                self.input.placeholder = self.T.answer_placeholder || '';
                if (heard && !sent) { sent = true; self.send({ text: heard, via: 'voice' }); }
            },
            onFatal: function (reason, detail) {
                self.listening = false;
                self.root.classList.remove('vgc-listening');
                self.micLabel(false);
                if (auto) return; /* автослушание после ответа — молча; кнопка микрофона на месте */
                if (reason === 'permission') self.setStatus(self.T.err_permission);
                else if (reason === 'backend' && detail && detail.code === 'not-allowed') self.setStatus(self.T.err_backend);
                else self.setStatus(self.T.err_speech);
            }
        });
        this.listening = true;
        this.root.classList.add('vgc-listening');
        this.micLabel(true);
        try { this.session.start(); } catch (e) { this.listening = false; this.root.classList.remove('vgc-listening'); this.micLabel(false); }
    };

    Console.prototype.stopListening = function () {
        if (this.session && this.listening) {
            try { this.session.stop(); } catch (e) {}
        }
    };

    function boot() {
        var roots = doc.querySelectorAll('[data-vgc]');
        var made = [];
        for (var i = 0; i < roots.length; i++) {
            if (roots[i].__vgc) continue;
            var c = new Console(roots[i]);
            roots[i].__vgc = c;
            made.push(c);
            if (global.__toolSettings) {
                var pairs = [['[data-vgc-speak]', 'speak', true], ['[data-vgc-listen]', 'listen', true],
                             ['[data-vgc-voice]', 'narrator', c.narrator], ['[data-vgc-rate]', 'rate', '1']];
                for (var p = 0; p < pairs.length; p++) {
                    var el = roots[i].querySelector(pairs[p][0]);
                    try { if (el) global.__toolSettings.bind(SLUG, el, pairs[p][1], pairs[p][2]); } catch (e) {}
                }
            }
            c.applySettings();
        }
        global.__voiceGameConsole = made[0] || null;
    }

    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot);
    else boot();
})(window, document);
